import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { AgentChatMessage, AgentConversation } from './Agent.types';
import { AgentActionType, AgentChatMessageType } from './Agent.types';
import { addChat, getChats, getAgentConfig, createChat } from './Agent.actions';
import type { AgentConfig } from 'configuration-schemas/viewdefns/viewdefn';

export interface AgentState {
  freeformAsyncState: 'loading' | 'unloaded' | 'loaded' | 'error';
  actionMode: 'automatic' | 'user';
  conversations: Map<string, AgentConversation>;
  viewDefn: AgentConfig | null;
}

const initAgentState: AgentState = {
  freeformAsyncState: 'unloaded',
  actionMode: 'user',
  conversations: new Map(),
  viewDefn: null,
};

type AgentChatPayload = { convoId: string; message: AgentChatMessage };

export const agentSlice = createSlice({
  name: 'agent',
  initialState: initAgentState,
  reducers: {
    resetAgent: () => initAgentState,
    setActionMode: (state, action: PayloadAction<'automatic' | 'user'>) => {
      state.actionMode = action.payload;
    },
    receiveConversations: (state, action: PayloadAction<AgentConversation[]>) => {
      state.conversations = new Map(action.payload.map((c) => [c.convoId, c]));
    },
    receiveAgentMessage: (state, action: PayloadAction<AgentChatMessage>) => {
      const conversation = state.conversations.get(action.payload.convoId);
      if (conversation) {
        conversation.messages.push(action.payload);
      }
    },
    mockRequestScope: (state) => {
      const mockActionMessage: AgentChatMessage = {
        user: 'agent',
        message: 'refresh da scope',
        type: AgentChatMessageType.agent,
        convoId: 'aaa',
        actions: [{ type: AgentActionType.scopeRefresh }],
      };
      if (state.conversations.size === 0) {
        const newcon: AgentConversation = { convoId: 'aaa', messages: [] };
        newcon.messages.push(mockActionMessage);
        const convos = new Map();
        convos.set('aaa', newcon);
        state.conversations = convos;
      } else {
        const convo = state.conversations.values().next();
        mockActionMessage.convoId = convo.value?.convoId!;
        convo.value!.messages = [mockActionMessage];
      }
    },
    agentRequestScopeRefresh: () => {}, // handled in Agent.epic.ts
    agentNavigate: (_state, _action: PayloadAction<{ url: string }>) => {},
  },
  extraReducers: (builder) => {
    // #region "getChats"
    builder.addCase(getChats.pending, (state) => {
      state.freeformAsyncState = 'loading';
    });
    builder.addCase(getChats.fulfilled, (state, { payload }) => {
      if (state.conversations.size === 0) {
        state.conversations = new Map(payload.map((c) => [c.convoId, c]));
      }
      state.freeformAsyncState = 'loaded';
    });
    builder.addCase(getChats.rejected, (state) => {
      state.freeformAsyncState = 'error';
    });
    // #endregion
    // #region "addChat"
    builder.addCase(addChat.pending, (state) => {
      state.freeformAsyncState = 'loading';
    });
    builder.addCase(addChat.fulfilled, (state, action: { payload: AgentChatPayload }) => {
      const conversation = state.conversations.get(action.payload.convoId);
      if (conversation) {
        if (!conversation.messages) {
          conversation.messages = [action.payload.message];
        } else {
          conversation.messages.push(action.payload.message);
        }
      }
      state.freeformAsyncState = 'loaded';
    });
    builder.addCase(addChat.rejected, (state) => {
      state.freeformAsyncState = 'error';
    });
    // #endregion

    // #region "getAgentConfig"
    builder.addCase(getAgentConfig.pending, (state) => {
      state.freeformAsyncState = 'loading';
    });
    builder.addCase(getAgentConfig.fulfilled, (state, { payload }) => {
      state.viewDefn = payload;
      state.freeformAsyncState = 'loaded';
    });
    builder.addCase(getAgentConfig.rejected, (state) => {
      state.freeformAsyncState = 'error';
    });
    // #endregion

    // #region "createChat"
    builder.addCase(createChat.pending, (state) => {
      state.freeformAsyncState = 'loading';
    });
    builder.addCase(createChat.fulfilled, (state, { payload }) => {
      const newConversation: AgentConversation = {
        convoId: payload.convoId,
        messages: [],
      };
      state.conversations.set(newConversation.convoId, newConversation);
      state.freeformAsyncState = 'loaded';
    });
    builder.addCase(createChat.rejected, (state) => {
      state.freeformAsyncState = 'error';
    });
    // #endregion
  },
});

export const {
  resetAgent,
  setActionMode,
  receiveConversations,
  receiveAgentMessage,
  mockRequestScope,
  agentRequestScopeRefresh,
  agentNavigate,
} = agentSlice.actions;

export default agentSlice.reducer;
